import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SITE_URL, site } from '@/data/site'
import { defaultSeo, resolveSeo, type SeoEntry } from '@/data/seo'

interface SeoProps {
  /** Override or extend the metadata resolved for the current route */
  override?: Partial<SeoEntry>
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Open Graph and Twitter images must be absolute URLs — crawlers and social
 * scrapers cannot resolve a root-relative path. Content overrides frequently
 * pass `/assets/...`, so normalize anything relative against SITE_URL.
 */
function absoluteUrl(value?: string): string | undefined {
  if (!value) return undefined
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('//')) return `https:${value}`
  return `${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`
}

/**
 * Lightweight SEO manager — updates document title, description, canonical,
 * Open Graph / Twitter tags and JSON-LD structured data per route.
 */
export function Seo({ override }: SeoProps) {
  const { pathname } = useLocation()

  const entry: SeoEntry = { ...defaultSeo, ...resolveSeo(pathname), ...override }
  const url = entry.canonical?.trim() || `${SITE_URL}${pathname}`
  const image = absoluteUrl(entry.image)

  useEffect(() => {
    document.title = entry.title
    document.documentElement.lang = 'en'

    upsertMeta('name', 'description', entry.description)
    upsertMeta('name', 'robots', entry.noIndex ? 'noindex, nofollow' : 'index, follow')
    upsertLink('canonical', url)

    // Open Graph
    upsertMeta('property', 'og:site_name', site.name)
    upsertMeta('property', 'og:type', entry.type ?? 'website')
    upsertMeta('property', 'og:title', entry.title)
    upsertMeta('property', 'og:description', entry.description)
    upsertMeta('property', 'og:url', url)
    if (image) upsertMeta('property', 'og:image', image)

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', entry.title)
    upsertMeta('name', 'twitter:description', entry.description)
    if (image) upsertMeta('name', 'twitter:image', image)
  }, [entry.title, entry.description, entry.type, entry.noIndex, url, image])

  useEffect(() => {
    const allDays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ]
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      name: site.name,
      description: defaultSeo.description,
      url: SITE_URL,
      image: absoluteUrl(defaultSeo.image),
      telephone: site.phone,
      email: site.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: site.address,
        addressLocality: 'Chauddagram',
        addressRegion: 'Chittagong',
        addressCountry: 'BD',
      },
      // Valid schema.org opening hours (the display string in `site.hours`
      // cannot be parsed by search engines).
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: allDays,
          opens: '00:00',
          closes: '23:59',
          description: 'Emergency department — open 24/7',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: allDays,
          opens: '08:00',
          closes: '20:00',
          description: 'Outdoor consultation',
        },
      ],
    }

    let script = document.head.querySelector<HTMLScriptElement>('#seo-jsonld')
    if (!script) {
      script = document.createElement('script')
      script.id = 'seo-jsonld'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(jsonLd)
  }, [])

  return null
}
